import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import decorSurlignage from '../../assets/images/decor_surlignage.png';
import decorGold from '../../assets/images/decor_surlignage.png';
import textDecoration from '../../assets/images/separateur.png';
import Logo from '../../components/common/Logo';

const Decoration = ({ type = 'surlignage', className = '' }) => {
  const decorationMap = {
    surlignage: decorSurlignage,
    gold: decorGold,
    text: textDecoration
  };

  const decorationSource = decorationMap[type] || decorSurlignage;

  return (
    <div className={`decoration-container ${className}`}>
      <img 
        src={decorationSource} 
        alt={`Décoration ${type}`}
        className="w-full h-auto"
      />
    </div>
  );
};

// Export des décorations individuelles pour utilisation directe
export const DecorSurlignage = ({ className = '' }) => (
  <img src={decorSurlignage} alt="Décoration surlignage" className={className} />
);

export const DecorGold = ({ className = '' }) => (
  <img src={decorGold} alt="Décoration dorée" className={className} />
);

export const TextDecoration = ({ className = '' }) => (
  <img src={textDecoration} alt="Décoration texte" className={className} />
);


/**
 * Séparateur décoratif élégant (ligne + icône centrale)
 */
export const SeparateurDecoratif = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: 3,
      }}
    >
      <Box sx={{ flex: 1, height: '2px', bgcolor: '#D4AF37' }} />
      <Box
        sx={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          bgcolor: '#D4AF37',
          mx: 1,
        }}
      />
      <Box sx={{ flex: 1, height: '2px', bgcolor: '#D4AF37' }} />
    </Box>
  );
};

/**
 * Header pour les pages d'authentification (Login/Register)
 * Version compacte sans le logo qui gêne le formulaire
 */
export const HomeHeader = () => {
  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        color: 'white',
        py: { xs: 2, md: 3 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
        mb: 3
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {/* TEXTE centré et animé - VERSION COMPACTE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ textAlign: 'center', width: '100%' }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#D4AF37',
              fontWeight: 'bold',
              letterSpacing: 1,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              textShadow: '0 0 10px rgba(212,175,55,0.4)',
            }}
          >
            BON'EPEY'A NTOKO
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'white',
              opacity: 0.9,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              mt: 0.5
            }}
          >
            Jakotonè la mboa (Association de Solidarité Familiale)
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
};

/**
 * Header complet avec logo pour la page d'accueil
 * Avec logo animé sur le côté - VERSION CORRIGÉE
 */
export const HomeHeaderFull = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.98) 50%, rgba(255,255,255,0.95) 100%)',
        color: '#000',
        py: { xs: 4, md: 6 },
        px: 2,
        position: 'relative',
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          // Sur téléphone, logo au-dessus du texte : côte à côte, le titre sortait de l'écran
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center', // Aligne le logo et le texte sur le même axe horizontal
          justifyContent: 'space-between',
          position: 'relative',
          minHeight: { xs: '120px', md: '180px' }, // Augmenté pour accueillir le logo
          gap: { xs: 2, md: 3 },
        }}
      >
        {/* LOGO animé - maintenant avec flexbox */}
        <motion.div
          initial={{ x: -60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0, // Empêche le logo de rétrécir
          }}
        >
          <Logo size="medium" />
        </motion.div>

        {/* TEXTE centré et animé */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
          style={{ 
            textAlign: 'center', 
            flex: 1, // Prend tout l'espace disponible
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#D4AF37',
              fontWeight: 'bold',
              letterSpacing: 1,
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
              textShadow: '0 0 10px rgba(212,175,55,0.4)',
            }}
          >
            BON'EPEY'A NTOKO
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#000000',
              opacity: 0.85,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              mt: 1,
              fontWeight: 400,
            }}
          >
            Jakotonè la mboa (Association de Solidarité Familiale)
          </Typography>
        </motion.div>

        {/* Espace vide à droite pour équilibrer la composition */}
        <Box sx={{ width: { xs: 0, md: '150px' }, flexShrink: 0 }} />
      </Container>
    </Box>
  );
};

export default Decoration;
